import { esc, resolveLogoDataUri, waitForPopupImages, waitForPopupReady } from './print-shared';

import type { ClinicBranding } from './useClinicIdentity';

type Translate = (key: string) => string;

export interface RxPrintLine {
  display_order: number;
  drug_name: string;
  dose?: string | null;
  dose_unit?: string | null;
  route: string;
  frequency?: string | null;
  duration_days?: number | null;
  instructions?: string | null;
  is_controlled?: boolean;
}

export interface RxPrintContext {
  patient_name: string;
  species: string | null;
  vet_name: string;
  issued_date: string;
  visit_folio: string;
  rx_reference: string;
}

function contactRows(branding: ClinicBranding): string {
  const rows: string[] = [];
  if (branding.address_line) rows.push(esc(branding.address_line));
  const phones = [branding.phone_primary, branding.phone_secondary].filter(Boolean).join(' / ');
  if (phones) rows.push(esc(phones));
  const social = [branding.email, branding.website, branding.facebook_handle]
    .filter(Boolean)
    .map((v) => esc(v))
    .join(' &middot; ');
  if (social) rows.push(social);
  if (rows.length === 0) return '';
  const badge = branding.has_24h_service ? '<span class="badge-24h">24 h Urgencias</span>' : '';
  return `<div class="contact">${rows.map((r) => `<p>${r}</p>`).join('')}${badge}</div>`;
}

/**
 * Opens a print-ready prescription document in a new window and triggers the browser
 * print dialog. No external PDF dependency — plain HTML + a @media print stylesheet.
 * Returns false if the popup was blocked so the caller can surface a message.
 */
export async function printPrescription(
  branding: ClinicBranding,
  ctx: RxPrintContext,
  lines: RxPrintLine[],
  t: Translate,
): Promise<boolean> {
  const ordered = [...lines].sort((a, b) => a.display_order - b.display_order);
  const logo_data_uri = await resolveLogoDataUri(branding.logo_url);

  const lineHtml = ordered
    .map((l, i) => {
      const parts: string[] = [];
      if (l.dose) parts.push(`${esc(l.dose)}${l.dose_unit ? ' ' + esc(l.dose_unit) : ''}`);
      parts.push(esc(t(`clinical.route_${l.route}`)));
      if (l.frequency) parts.push(esc(l.frequency));
      if (l.duration_days) parts.push(`${l.duration_days} ${esc(t('clinical.rx_doc_days'))}`);
      const controlled = l.is_controlled
        ? `<span class="ctrl">▲ ${esc(t('clinical.rx_doc_controlled'))}</span>`
        : '';
      const instructions = l.instructions
        ? `<div class="instr">${esc(t('clinical.rx_doc_instructions'))}: ${esc(l.instructions)}</div>`
        : '';
      return `<li>
        <div class="drug"><span class="n">${i + 1}.</span> <strong>${esc(l.drug_name)}</strong> ${controlled}</div>
        <div class="meta">${parts.join(' &middot; ')}</div>
        ${instructions}
      </li>`;
    })
    .join('');

  const species = ctx.species ? ` (${esc(ctx.species)})` : '';
  const brand = esc(branding.brand_color_hex);
  const logo_markup = logo_data_uri
    ? `<img class="logo" src="${esc(logo_data_uri)}" alt="${esc(branding.clinic_name)}" />`
    : '';

  const html = `<!doctype html>
<html lang="es">
<head>
<meta charset="utf-8" />
<title>${esc(t('clinical.rx_doc_title'))} — ${esc(ctx.patient_name)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #111; margin: 32px; }
  .logo-wrap { text-align: center; margin-bottom: 6px; }
  .logo { max-width: 220px; max-height: 90px; }
  .clinic { font-size: 18px; font-weight: 700; color: ${brand}; text-align: center; }
  .doc-title { font-size: 15px; font-weight: 600; margin-top: 2px; color: #444; text-align: center; }
  header { border-bottom: 2px solid ${brand}; padding-bottom: 10px; margin-bottom: 14px; }
  .info { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 16px; font-size: 13px; margin-bottom: 16px; }
  .info .lbl { color: #666; }
  ol { list-style: none; padding: 0; margin: 0; }
  li { border: 1px solid #ddd; border-radius: 6px; padding: 8px 10px; margin-bottom: 8px; }
  .drug { font-size: 14px; }
  .drug .n { color: #666; }
  .meta { font-size: 12px; color: #333; margin-top: 2px; }
  .instr { font-size: 12px; color: #444; margin-top: 3px; font-style: italic; }
  .ctrl { color: #b00; font-size: 11px; font-weight: 600; margin-left: 6px; }
  footer { margin-top: 20px; padding-top: 8px; border-top: 1px solid #ccc; font-size: 11px; color: #555; display: flex; justify-content: space-between; }
  .contact { margin-top: 10px; padding-top: 8px; border-top: 1px solid #ccc; text-align: center; font-size: 11px; color: #555; }
  .contact p { margin: 2px 0; }
  .badge-24h { display: inline-block; margin-top: 4px; padding: 2px 8px; border-radius: 999px; background: ${brand}; color: #fff; font-size: 10px; font-weight: 700; }
  .empty { color: #777; font-size: 13px; }
  @media print { body { margin: 0; padding: 18mm; } @page { margin: 0; } }
</style>
</head>
<body>
  <header>
    ${logo_markup ? `<div class="logo-wrap">${logo_markup}</div>` : ''}
    <div class="clinic">${esc(branding.clinic_name)}</div>
    <div class="doc-title">${esc(t('clinical.rx_doc_title'))}</div>
  </header>
  <div class="info">
    <div><span class="lbl">${esc(t('clinical.rx_doc_patient'))}:</span> ${esc(ctx.patient_name)}${species}</div>
    <div><span class="lbl">${esc(t('clinical.rx_doc_date'))}:</span> ${esc(ctx.issued_date)}</div>
    <div><span class="lbl">${esc(t('clinical.rx_doc_vet'))}:</span> ${esc(ctx.vet_name)}</div>
    <div><span class="lbl">${esc(t('clinical.rx_doc_reference'))}:</span> ${esc(ctx.rx_reference)}</div>
  </div>
  ${ordered.length === 0 ? `<p class="empty">${esc(t('clinical.no_rx_lines'))}</p>` : `<ol>${lineHtml}</ol>`}
  <footer>
    <span>${esc(t('clinical.rx_doc_visit_folio'))}: ${esc(ctx.visit_folio)}</span>
    <span>${esc(t('clinical.rx_doc_reference'))}: ${esc(ctx.rx_reference)}</span>
  </footer>
  ${contactRows(branding)}
</body>
</html>`;

  const w = window.open('', '_blank', 'width=820,height=900');
  if (!w) return false;
  w.opener = null; // sever the same-origin back-reference (handle stays usable)
  w.document.write(html);
  w.document.close();
  await waitForPopupReady(w);
  await waitForPopupImages(w);
  w.print();
  return true;
}
